import { MAX_OCCURRENCES_PER_QUERY, TERMINAL_STATUSES } from "@dream/config/constants";
import { applyDuration, durationMs, rangesOverlap } from "../time/timezone.ts";
import type {
  DateRange,
  Deliverable,
  DeliverableOccurrence,
  Instant,
  RecurrenceRule,
  ResolvedOccurrence,
} from "../types.ts";
import { expandRruleInRange } from "./rrule.ts";

/**
 * Resolução de ocorrências: série + divergências → lista pronta para render.
 *
 * O banco guarda a entrega mestre e a regra. Só o que **divergiu** da série
 * existe como linha (`override` ou `exception`). Esta função é a única fonte da
 * verdade sobre "o que aparece no calendário", e roda igual na API e no worker.
 */

export interface DeliverableWithRecurrence {
  deliverable: Deliverable;
  recurrence: RecurrenceRule | null;
  occurrences: DeliverableOccurrence[];
}

export interface ExpandContext {
  range: DateRange;
  /** Instante considerado "agora", para marcar atraso. Injetado para testabilidade. */
  now: Instant;
  limit?: number;
}

/** ID composto e estável de uma ocorrência. Sobrevive a reload e a reordenação. */
export function occurrenceId(deliverableId: string, occurrenceStart: Instant): string {
  return `${deliverableId}:${occurrenceStart.toISOString()}`;
}

export function parseOccurrenceId(id: string): {
  deliverableId: string;
  occurrenceStart: Instant;
} {
  const separator = id.indexOf(":");
  if (separator === -1) {
    return { deliverableId: id, occurrenceStart: new Date(Number.NaN) };
  }
  return {
    deliverableId: id.slice(0, separator),
    occurrenceStart: new Date(id.slice(separator + 1)),
  };
}

/**
 * Atraso é derivado, nunca armazenado.
 *
 * Guardar `late` como status persistido exigiria um job varrendo a base a cada
 * minuto e produziria linhas desatualizadas entre execuções. Calcular na
 * leitura é sempre correto. O status `late` existe no enum apenas para quando a
 * equipe quiser marcá-lo manualmente.
 */
export function isOccurrenceLate(
  occurrence: { endsAt: Instant; status: ResolvedOccurrence["status"] },
  now: Instant,
): boolean {
  if (TERMINAL_STATUSES.includes(occurrence.status)) return false;
  if (occurrence.status === "cancelled") return false;
  return occurrence.endsAt.getTime() < now.getTime();
}

function buildResolved(
  deliverable: Deliverable,
  occurrenceStart: Instant,
  startsAt: Instant,
  endsAt: Instant,
  override: DeliverableOccurrence | undefined,
  isRecurring: boolean,
  now: Instant,
): ResolvedOccurrence {
  const status = override?.status ?? deliverable.status;
  return {
    occurrenceId: occurrenceId(deliverable.id, occurrenceStart),
    deliverableId: deliverable.id,
    occurrenceStart,
    isRecurring,
    isOverride: override !== undefined,
    title: override?.title ?? deliverable.title,
    description: override?.description ?? deliverable.description,
    status,
    priority: deliverable.priority,
    assigneeId: override?.assigneeId ?? deliverable.assigneeId,
    startsAt,
    endsAt,
    isAllDay: deliverable.isAllDay,
    timezone: deliverable.timezone,
    publishAt: deliverable.publishAt,
    clientId: deliverable.clientId,
    projectId: deliverable.projectId,
    typeId: deliverable.typeId,
    visibleToClient: deliverable.visibleToClient,
    links: deliverable.links,
    version: override?.version ?? deliverable.version,
    isLate: isOccurrenceLate({ endsAt, status }, now),
  };
}

/**
 * Expande uma entrega (recorrente ou não) dentro da janela.
 *
 * Ordem de aplicação, que importa:
 *  1. exceções removem a ocorrência antes de qualquer outro processamento;
 *  2. overrides substituem campos da ocorrência gerada;
 *  3. overrides que **saíram** da janela são descartados, e overrides que
 *     **entraram** na janela são incluídos mesmo que a RRULE não os produza
 *     naquele intervalo — sem isso, arrastar uma ocorrência para o mês seguinte
 *     a faria desaparecer das duas visões.
 */
export function expandDeliverable(
  input: DeliverableWithRecurrence,
  context: ExpandContext,
): ResolvedOccurrence[] {
  const { deliverable, recurrence, occurrences } = input;
  const { range, now } = context;
  const limit = context.limit ?? MAX_OCCURRENCES_PER_QUERY;

  const overridesByStart = new Map<number, DeliverableOccurrence>();
  const exceptionStamps = new Set<number>();

  for (const occurrence of occurrences) {
    const stamp = occurrence.occurrenceStart.getTime();
    if (occurrence.kind === "exception") {
      exceptionStamps.add(stamp);
    } else {
      overridesByStart.set(stamp, occurrence);
    }
  }

  if (recurrence !== null) {
    for (const exception of recurrence.exceptions) {
      exceptionStamps.add(exception.getTime());
    }
  }

  // Entrega simples: uma única ocorrência, cujo occurrenceStart é o próprio início.
  if (recurrence === null) {
    const override = overridesByStart.get(deliverable.startsAt.getTime());
    if (exceptionStamps.has(deliverable.startsAt.getTime())) return [];

    const startsAt = override?.startsAt ?? deliverable.startsAt;
    const endsAt = override?.endsAt ?? deliverable.endsAt;
    if (!rangesOverlap({ start: startsAt, end: endsAt }, range)) return [];

    return [
      buildResolved(deliverable, deliverable.startsAt, startsAt, endsAt, override, false, now),
    ];
  }

  const baseDurationMs = durationMs(deliverable.startsAt, deliverable.endsAt);

  const starts = expandRruleInRange({
    rrule: recurrence.rrule,
    timezone: recurrence.timezone,
    dtstart: deliverable.startsAt,
    rangeStart: range.start,
    rangeEnd: range.end,
    durationMs: baseDurationMs,
    limit,
  });

  const resolved: ResolvedOccurrence[] = [];
  const emitted = new Set<number>();

  for (const occurrenceStart of starts) {
    const stamp = occurrenceStart.getTime();
    if (exceptionStamps.has(stamp)) continue;

    const override = overridesByStart.get(stamp);
    const startsAt = override?.startsAt ?? occurrenceStart;
    const endsAt =
      override?.endsAt ??
      applyDuration(
        startsAt,
        { start: deliverable.startsAt, end: deliverable.endsAt },
        deliverable.timezone,
      );

    // Um override pode ter movido a ocorrência fora da janela consultada.
    if (!rangesOverlap({ start: startsAt, end: endsAt }, range)) continue;

    emitted.add(stamp);
    resolved.push(
      buildResolved(deliverable, occurrenceStart, startsAt, endsAt, override, true, now),
    );
  }

  // Overrides cujo occurrenceStart original está fora da janela, mas que foram
  // movidos para dentro dela.
  for (const [stamp, override] of overridesByStart) {
    if (emitted.has(stamp) || exceptionStamps.has(stamp)) continue;

    const startsAt = override.startsAt ?? new Date(stamp);
    const endsAt =
      override.endsAt ??
      applyDuration(
        startsAt,
        { start: deliverable.startsAt, end: deliverable.endsAt },
        deliverable.timezone,
      );

    if (!rangesOverlap({ start: startsAt, end: endsAt }, range)) continue;
    if (resolved.length >= limit) break;

    resolved.push(
      buildResolved(deliverable, new Date(stamp), startsAt, endsAt, override, true, now),
    );
  }

  resolved.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
  return resolved;
}

/** Expande várias entregas e devolve tudo ordenado por início. */
export function expandDeliverables(
  inputs: DeliverableWithRecurrence[],
  context: ExpandContext,
): ResolvedOccurrence[] {
  const all: ResolvedOccurrence[] = [];
  for (const input of inputs) {
    all.push(...expandDeliverable(input, context));
  }
  all.sort((a, b) => {
    const delta = a.startsAt.getTime() - b.startsAt.getTime();
    return delta !== 0 ? delta : a.occurrenceId.localeCompare(b.occurrenceId);
  });
  return all;
}
